import React, { useEffect, useState } from "react";
import {
  Card,
  BlockStack,
  InlineGrid,
  Text,
  Badge,
  Spinner,
  Divider,
} from "@shopify/polaris";
import supabase from "../../../supabase-client";
import { formatPrice, formatPlate } from "../../../utility/funcs";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../../context/BranchContext";

function FleetOverview() {
  const navigate = useNavigate();
  const { branch } = useBranch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      const { data: result, error } = await supabase.rpc("get_fleet_dashboard", {
        p_branch_id: branch?.id || null,
      });
      if (error) {
        console.error(error.message);
        setLoading(false);
        return;
      }
      setData(result);
      setLoading(false);
    };
    fetchDashboard();
  }, [branch]);

  if (loading) return <Spinner size="small" />;
  if (!data) return null;

  const StatCard = ({ label, value, tone, onClick, percent, subtitle }) => (
    <Card>
      <div
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default", textAlign: "center" }}
      >
        <Text variant="headingXl" as="p">
          {value}
          {percent != null && (
            <Text as="span" variant="bodySm" tone="subdued">
              {" "}
              ({percent}%)
            </Text>
          )}
        </Text>
        <Text variant="bodySm" tone={tone || "subdued"}>
          {label}
        </Text>
        {subtitle && (
          <Text variant="bodySm" tone="subdued" as="p">
            {subtitle}
          </Text>
        )}
      </div>
    </Card>
  );

  const pct = (n) => (data.total_cars > 0 ? Math.round((n / data.total_cars) * 100) : 0);

  const ResList = ({ title, items, badgeTone, badgeLabel }) => {
    if (!items || items.length === 0) return null;
    return (
      <Card>
        <BlockStack gap="200">
          <Text variant="headingSm">
            {title}{" "}
            <Badge tone={badgeTone}>{items.length}</Badge>
          </Text>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.25rem 0",
                cursor: "pointer",
                borderBottom: "1px solid var(--p-color-border-secondary)",
              }}
              onClick={() => navigate(`/reservation?id=${item.id}`)}
            >
              <BlockStack gap="050">
                <Text variant="bodySm" fontWeight="semibold">
                  {item.customer_name || "Servicio"}
                </Text>
                <Text variant="bodySm" tone="subdued">
                  {item.model} — {formatPlate(item.plate)}
                </Text>
              </BlockStack>
              {badgeLabel && <Badge tone={badgeTone}>{badgeLabel}</Badge>}
            </div>
          ))}
        </BlockStack>
      </Card>
    );
  };

  return (
    <BlockStack gap="300">
      <Text variant="headingMd">Flota</Text>
      <InlineGrid columns={4} gap="300">
        <StatCard
          label="Total Autos"
          value={data.total_cars}
          subtitle={
            data.rented_subleases > 0
              ? `${data.total_owned} + ${data.rented_subleases}`
              : null
          }
        />
        <StatCard
          label="Rentados"
          value={data.rented}
          tone="info"
          percent={pct(data.rented)}
          subtitle={
            data.rented_subleases > 0
              ? `${data.rented_owned} + ${data.rented_subleases}`
              : null
          }
        />
        <StatCard label="En Servicio" value={data.in_service} tone="warning" percent={pct(data.in_service)} />
        <StatCard label="Disponibles" value={data.available} tone="success" percent={pct(data.available)} />
      </InlineGrid>

      <InlineGrid columns={2} gap="300">
        <Card>
          <Text variant="headingSm">Ingresos del Mes</Text>
          <Text variant="headingLg">{formatPrice(data.revenue_month)}</Text>
        </Card>
        <StatCard
          label="Entregas Hoy"
          value={data.checkout_today?.length || 0}
        />
      </InlineGrid>

      <InlineGrid columns={data.overdue?.length > 0 ? 2 : 1} gap="300">
        <ResList
          title="Devoluciones Hoy"
          items={data.returning_today}
          badgeTone="info"
        />
        <ResList
          title="Vencidas"
          items={data.overdue}
          badgeTone="critical"
          badgeLabel="Vencida"
        />
      </InlineGrid>

      {data.checkout_today?.length > 0 && (
        <ResList
          title="Entregas Pendientes Hoy"
          items={data.checkout_today}
          badgeTone="attention"
          badgeLabel="Pendiente"
        />
      )}
    </BlockStack>
  );
}

export default FleetOverview;
